import React, { useEffect, useState } from 'react';
import { PARTNER_CITIES } from '../../content';
import { portalCopy } from '../copy';
import { mapPlaceToBusinessImport } from '../places/mapPlaceToBusiness';
import type { PlaceDetails } from '../places/types';
import type { PartnerBusiness, WizardBasicsSubmit, WizardPreviewDraft } from '../types';
import GooglePlaceSearch from './GooglePlaceSearch';
import { PortalButton, PortalField, portalInputClass, portalLabelClass } from './PortalUi';

type WizardStepBasicsProps = {
  business: PartnerBusiness | null;
  onSubmit: (data: WizardBasicsSubmit) => void;
  onPreviewChange?: (draft: WizardPreviewDraft) => void;
};

type Phase = 'search' | 'review';

type ReadOnlyFieldProps = {
  label: string;
  value: string;
  hint?: string;
};

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value, hint }) => (
  <div>
    <span className={portalLabelClass}>{label}</span>
    {hint && <p className="mb-1.5 font-jakarta text-xs leading-relaxed text-[#5a3158]">{hint}</p>}
    <p className="rounded-2xl border border-transparent bg-[#fcf7fa] px-4 py-3 font-jakarta text-[15px] text-[#2a0f2a]">
      {value}
    </p>
  </div>
);

const WizardStepBasics: React.FC<WizardStepBasicsProps> = ({ business, onSubmit, onPreviewChange }) => {
  const hasExistingBasics = Boolean(business?.name && business.address);

  const [phase, setPhase] = useState<Phase>(hasExistingBasics ? 'review' : 'search');
  const [importedPlace, setImportedPlace] = useState<PlaceDetails | null>(null);
  const [categoryNeedsPick, setCategoryNeedsPick] = useState(false);

  const [name, setName] = useState(business?.name ?? '');
  const [cityId, setCityId] = useState<PartnerBusiness['cityId']>(business?.cityId ?? 'miami');
  const [category, setCategory] = useState<PartnerBusiness['category'] | ''>(business?.category ?? '');
  const [address, setAddress] = useState(business?.address ?? '');
  const [googlePlaceId, setGooglePlaceId] = useState(business?.googlePlaceId);
  const [googleMapsUrl, setGoogleMapsUrl] = useState(business?.googleMapsUrl);
  const [importedPhotoUrls, setImportedPhotoUrls] = useState<string[]>(business?.importedPhotoUrls ?? []);
  const [prefillDescription, setPrefillDescription] = useState(business?.description ?? '');
  const [prefillWebsite, setPrefillWebsite] = useState(business?.website ?? '');
  const [prefillHours, setPrefillHours] = useState(business?.hours);

  const cityLabel = PARTNER_CITIES.find((city) => city.id === cityId)?.label ?? cityId;

  useEffect(() => {
    onPreviewChange?.({
      name,
      cityId: cityId || business?.cityId || 'miami',
      category: (category || business?.category || 'food') as PartnerBusiness['category'],
      address,
      description: prefillDescription,
      website: prefillWebsite,
      hours: prefillHours,
      photoCount: importedPhotoUrls.length,
      photoPreviewUrls: importedPhotoUrls,
    });
  }, [
    name,
    cityId,
    category,
    address,
    prefillDescription,
    prefillWebsite,
    prefillHours,
    importedPhotoUrls,
    business?.cityId,
    business?.category,
    onPreviewChange,
  ]);

  const applyPlaceImport = (place: PlaceDetails) => {
    const mapped = mapPlaceToBusinessImport(place, cityId);
    setImportedPlace(place);
    setName(mapped.name);
    setAddress(mapped.address);
    setCityId(mapped.cityId);
    setGooglePlaceId(mapped.googlePlaceId);
    setGoogleMapsUrl(mapped.googleMapsUrl);
    setImportedPhotoUrls(mapped.importedPhotoUrls);
    setPrefillDescription(mapped.description);
    setPrefillWebsite(mapped.website);
    setPrefillHours(mapped.hours);

    if (mapped.category) {
      setCategory(mapped.category);
      setCategoryNeedsPick(false);
    } else {
      setCategory('');
      setCategoryNeedsPick(true);
    }

    setPhase('review');
  };

  const resetToSearch = () => {
    setPhase('search');
    setImportedPlace(null);
    setCategoryNeedsPick(false);
    setName('');
    setAddress('');
    setCategory('');
    setGooglePlaceId(undefined);
    setGoogleMapsUrl(undefined);
    setImportedPhotoUrls([]);
    setPrefillDescription('');
    setPrefillWebsite('');
    setPrefillHours(undefined);
  };

  const canSubmit =
    name.trim().length >= 2 &&
    address.trim().length >= 5 &&
    (category === 'food' || category === 'wellness');

  if (phase === 'search') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-instrument text-3xl italic tracking-[-0.01em] text-[#2a0f2a]">
            {portalCopy.wizard.basics.title}
          </h2>
          <p className="mt-2 font-jakarta text-[15px] leading-relaxed text-[#5a3158]">
            {portalCopy.wizard.basics.subtitle}
          </p>
        </div>

        <div className="space-y-5 rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white p-6">
          <PortalField label={portalCopy.wizard.basics.city} htmlFor="business-city-search">
            <select
              id="business-city-search"
              className={portalInputClass}
              value={cityId}
              onChange={(event) => setCityId(event.target.value as PartnerBusiness['cityId'])}
            >
              {PARTNER_CITIES.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.label}
                </option>
              ))}
            </select>
          </PortalField>
          <GooglePlaceSearch cityId={cityId} onResolved={applyPlaceImport} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-instrument text-3xl italic tracking-[-0.01em] text-[#2a0f2a]">
          {portalCopy.wizard.basics.reviewTitle}
        </h2>
        <p className="mt-2 font-jakarta text-[15px] leading-relaxed text-[#5a3158]">
          {portalCopy.wizard.basics.reviewSubtitle}
        </p>
      </div>

      {(importedPlace || googlePlaceId) && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-chisme-primary/20 bg-[#fcf7fa] px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-lg" aria-hidden>
              📍
            </span>
            <div className="min-w-0">
              <p className="font-jakarta text-sm font-semibold text-[#2a0f2a]">
                {portalCopy.wizard.basics.importedFromGoogle}
              </p>
              <p className="truncate font-jakarta text-xs text-[#5a3158]">
                {importedPlace?.name ?? name}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={resetToSearch}
            className="shrink-0 font-jakarta text-sm font-semibold text-chisme-primary hover:underline"
          >
            {portalCopy.wizard.basics.changePlace}
          </button>
        </div>
      )}

      <div className="space-y-5 rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white p-6">
        <PortalField label={portalCopy.wizard.basics.name} htmlFor="business-name">
          <input
            id="business-name"
            className={portalInputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={portalCopy.wizard.basics.namePlaceholder}
          />
        </PortalField>

        <PortalField
          label={portalCopy.wizard.basics.category}
          htmlFor="business-category"
          hint={categoryNeedsPick ? portalCopy.wizard.basics.categoryRequired : undefined}
        >
          <select
            id="business-category"
            className={portalInputClass}
            value={category}
            onChange={(e) => setCategory(e.target.value as PartnerBusiness['category'])}
          >
            {categoryNeedsPick && (
              <option value="" disabled>
                {portalCopy.wizard.basics.categoryPlaceholder}
              </option>
            )}
            <option value="food">{portalCopy.wizard.basics.categories.food}</option>
            <option value="wellness">{portalCopy.wizard.basics.categories.wellness}</option>
          </select>
        </PortalField>

        <div className="space-y-4 border-t border-[rgba(74,33,71,0.08)] pt-5">
          <p className="font-jakarta text-xs leading-relaxed text-[#5a3158]">
            {portalCopy.wizard.basics.locationSyncNote}
          </p>
          <ReadOnlyField label={portalCopy.wizard.basics.city} value={cityLabel} />
          <ReadOnlyField label={portalCopy.wizard.basics.address} value={address} />
        </div>
      </div>

      <PortalButton
        fullWidth
        disabled={!canSubmit}
        onClick={() =>
          onSubmit({
            name: name.trim(),
            cityId,
            category: category as PartnerBusiness['category'],
            address: address.trim(),
            googlePlaceId,
            googleMapsUrl,
            importedPhotoUrls,
            description: prefillDescription || undefined,
            website: prefillWebsite || undefined,
            hours: prefillHours,
          })
        }
      >
        {portalCopy.wizard.next}
      </PortalButton>
    </div>
  );
};

export default WizardStepBasics;
